// src/components/AutoScrollCarousel.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

const AutoScrollCarousel = () => {
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  
  const carouselItems = useMemo(() => [
    {
      id: 1,
      title: "World Cup 2026",
      subtitle: "Live Betting Available",
      image: "https://thumbs.dreamstime.com/b/vector-template-football-your-design-white-background-united-flags-canada-mexico-united-states-united-fifa-world-cup-119620039.jpg?w=768",
      ctaText: "Bet Now",
      ctaLink: "/sports/soccer"
    },
    {
      id: 2,
      title: "Champions League",
      subtitle: "Best Odds Guaranteed",
      image: "https://cdn.businessday.ng/2021/05/UEFA-Champions-League-final.jpg",
      ctaText: "Place Bets",
      ctaLink: "/live"
    },
    {
      id: 3,
      title: "NBA Playoffs",
      subtitle: "In-Play Betting",
      image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      ctaText: "View Odds",
      ctaLink: "/sports/basketball"
    },
    {
      id: 4,
      title: "Grand Slam Tennis",
      subtitle: "Live Streaming Available",
      image: "https://images.unsplash.com/photo-1595435934247-5d33b7f92c70?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      ctaText: "Watch Live",
      ctaLink: "/live/tennis"
    }
  ], []);

  const scrollToSlide = useCallback((index) => {
    if (!carouselRef.current) return;
    
    const itemWidth = carouselRef.current.children[0]?.clientWidth || 0;
    if (itemWidth > 0) {
      setCurrentIndex(index);
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth'
      });
    }
  }, []);

  const nextSlide = useCallback(() => {
    const nextIndex = (currentIndex + 1) % carouselItems.length;
    scrollToSlide(nextIndex);
  }, [currentIndex, carouselItems.length, scrollToSlide]);

  const startAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        nextSlide();
      }
    }, 5000);
  }, [isPaused, nextSlide]);

  useEffect(() => {
    if (carouselItems.length > 1) {
      startAutoScroll();
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [carouselItems.length, startAutoScroll]);

  const handleDotClick = useCallback((index) => {
    scrollToSlide(index);
    // Reset timer when manually navigating
    startAutoScroll();
  }, [scrollToSlide, startAutoScroll]);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  return (
    <section 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="Featured events carousel"
    >
      <div className="carousel-container overflow-hidden">
        <div 
          ref={carouselRef} 
          className="flex snap-x snap-mandatory scroll-smooth overflow-x-auto scrollbar-hide"
          role="list"
          aria-live="polite"
        >
          {carouselItems.map((item, index) => (
            <div 
              key={item.id}
              className="flex-shrink-0 w-full snap-center relative"
              role="listitem"
              aria-label={`Slide ${index + 1} of ${carouselItems.length}: ${item.title}`}
            >
              <div className="relative h-64 md:h-80 lg:h-96">
                <img 
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <div 
                  className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent"
                  aria-hidden="true"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="max-w-3xl mx-auto">
                    <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-lg md:text-xl text-gray-300 mb-4">
                      {item.subtitle}
                    </p>
                    <button 
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      onClick={() => window.location.href = item.ctaLink}
                    >
                      {item.ctaText}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {carouselItems.length > 1 && (
        <>
          {/* Navigation Dots */}
           <div 
      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-10"
      role="tablist"
      aria-label="Carousel navigation"
    >
      {carouselItems.map((_, index) => (
        <button
          key={index}
          style={{
            width: '12px',
            height: '12px',
            minWidth: '6px',
            minHeight: '6px'
          }}
          className={`rounded-full transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-white ${
            index === currentIndex 
              ? 'bg-blue-500 scale-125' 
              : 'bg-gray-500/80 hover:bg-gray-400'
          }`}
          onClick={() => handleDotClick(index)}
          aria-label={`Go to slide ${index + 1}`}
          aria-selected={index === currentIndex}
          role="tab"
        />
      ))}
    </div>
          
          {/* Previous/Next Buttons */}
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none">
            <button
              className="pointer-events-auto w-10 h-10 rounded-full bg-gray-900/60 hover:bg-gray-900/80 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              onClick={() => {
                const prevIndex = currentIndex === 0 ? carouselItems.length - 1 : currentIndex - 1;
                handleDotClick(prevIndex);
              }}
              aria-label="Previous slide"
            >
              ←
            </button>
            <button
              className="pointer-events-auto w-10 h-10 rounded-full bg-gray-900/60 hover:bg-gray-900/80 text-white flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              onClick={() => {
                const nextIndex = (currentIndex + 1) % carouselItems.length;
                handleDotClick(nextIndex);
              }}
              aria-label="Next slide"
            >
              →
            </button>
          </div>
          
          {/* Progress Indicator */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-900/30">
            <div 
              className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
              style={{
                width: `${(currentIndex / (carouselItems.length - 1)) * 100}%`
              }}
              aria-hidden="true"
            />
          </div>
        </>
      )}
    </section>
  );
};

export default AutoScrollCarousel;